import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FabricImage } from "@/components/FabricImage";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";
import { compressImage } from "@/lib/compressImage";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{children}</Label>
);

export const CompanyDialog = ({ open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({ company_name: "", address: "", phone: "", gstin: "", deals_in: "", logo_path: null });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/profile").then(({ data }) => setForm(data)).catch(() => {});
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    try {
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, logo_path: data.image_path }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/profile", form);
      toast.success("Company details saved");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="company-dialog" className="max-h-[92vh] overflow-y-auto rounded-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl font-extrabold tracking-tighter">Company details</DialogTitle>
          <DialogDescription className="sr-only">Company name, logo, address and phone used on all reports</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div
            data-testid="company-logo-dropzone"
            onClick={() => fileRef.current?.click()}
            className="cursor-pointer border-2 border-dashed border-zinc-300 p-4 text-center transition-colors duration-200 hover:border-zinc-950"
          >
            {form.logo_path ? (
              <div className="relative">
                <FabricImage path={form.logo_path} alt="Company logo" className="mx-auto h-24 object-contain" w={600} />
                <button
                  type="button"
                  data-testid="company-logo-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    setForm((f) => ({ ...f, logo_path: null }));
                  }}
                  className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center border border-zinc-300 bg-white text-zinc-500 transition-colors duration-200 hover:border-red-500 hover:text-red-600"
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div className="py-3 text-zinc-500">
                <UploadCloud className="mx-auto mb-2" size={20} />
                <p className="text-sm">{uploading ? "Uploading…" : "Upload company logo (used on all reports)"}</p>
              </div>
            )}
            <input
              data-testid="company-logo-input"
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => pickLogo(e.target.files?.[0])}
            />
          </div>

          <div>
            <L>Company Name</L>
            <Input data-testid="company-name-input" className="mt-2 rounded-sm" value={form.company_name ?? ""} onChange={set("company_name")} placeholder="Agarwal Synthetics" />
          </div>
          <div>
            <L>Address</L>
            <Textarea data-testid="company-address-input" rows={2} className="mt-2 rounded-sm" value={form.address ?? ""} onChange={set("address")} placeholder="Street, city, state" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <L>Phone</L>
              <Input data-testid="company-phone-input" className="mt-2 rounded-sm" value={form.phone ?? ""} onChange={set("phone")} placeholder="9264242777" />
            </div>
            <div>
              <L>GSTIN</L>
              <Input data-testid="company-gstin-input" className="mt-2 rounded-sm" value={form.gstin ?? ""} onChange={set("gstin")} placeholder="10ABCDE1234F1Z5" />
            </div>
            <div>
              <L>Deals In</L>
              <Input data-testid="company-deals-input" className="mt-2 rounded-sm" value={form.deals_in ?? ""} onChange={set("deals_in")} placeholder="Deals in all major shirting suiting companies" />
            </div>
          </div>

          <Button data-testid="company-save-button" type="submit" disabled={saving || uploading} className="w-full rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90">
            {saving ? "Saving…" : "Save company details"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
